import React, { useEffect, useRef, useState } from 'react';

import styles from './index.module.scss';

export interface EditableTitleProps {
  /** 当前标题 */
  title: string;
  /** 保存回调 */
  onSave: (newTitle: string) => Promise<boolean>;
  /** 自定义样式类名 */
  className?: string;
  /** 输入框样式类名 */
  inputClassName?: string;
  /** 最大长度 */
  maxLength?: number;
  /** 是否禁用编辑 */
  disabled?: boolean;
  /** 外部控制编辑状态 */
  isEditing?: boolean;
  /** 编辑状态改变回调 */
  onEditingChange?: (isEditing: boolean) => void;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onSave,
  className = '',
  inputClassName = '',
  maxLength = 50,
  disabled = false,
  isEditing: externalIsEditing,
  onEditingChange,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  // 使用外部控制或内部状态
  const isEditing =
    externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  // 同步外部 title 变化
  useEffect(() => {
    if (!isEditing) {
      setEditedTitle(title);
    }
  }, [title, isEditing]);

  // 自动聚焦
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const updateEditingState = (newState: boolean) => {
    if (externalIsEditing === undefined) {
      setInternalIsEditing(newState);
    }
    onEditingChange?.(newState);
  };

  const handleStartEdit = () => {
    if (disabled) return;
    setEditedTitle(title);
    updateEditingState(true);
  };

  const handleSave = async () => {
    const trimmedTitle = editedTitle.trim();

    // 如果标题为空或没有变化，取消编辑
    if (!trimmedTitle || trimmedTitle === title) {
      handleCancel();
      return;
    }

    // 调用保存回调
    const success = await onSave(trimmedTitle);

    if (success) {
      updateEditingState(false);
    } else {
      // 保存失败，恢复原标题
      setEditedTitle(title);
    }
  };

  const handleCancel = () => {
    setEditedTitle(title);
    updateEditingState(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        className={`${styles.titleInput} ${inputClassName}`}
        value={editedTitle}
        onChange={(e) => setEditedTitle(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <p
      className={`${styles.title} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        handleStartEdit();
      }}
      title={title}
    >
      {title}
    </p>
  );
};

export default EditableTitle;
